import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, LockSimple } from "@phosphor-icons/react";
import { api, apiError } from "../../lib/api";
import { inputCls, btnPrimary } from "../checklists/shared";

function Toggle({ on, onClick, testId, disabled }) {
  return (
    <button data-testid={testId} onClick={onClick} disabled={disabled}
      className={`relative h-6 w-10 shrink-0 rounded-full ${on ? "bg-emerald-500" : "bg-slate-300"} ${disabled ? "opacity-40" : "tap-press"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow ${on ? "left-[18px]" : "left-0.5"}`} />
    </button>
  );
}

export function BrandRolesPanel({ userRole }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ label: "", base: "regional" });
  const load = () => api.get("/roles").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  if (!data) return null;

  const patch = async (role, body, msg) => {
    try { await api.put(`/roles/${role.id}`, body); if (msg) toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const create = async () => {
    if (!form.label.trim()) return toast.error("Enter a role title");
    try { await api.post("/roles", form); toast.success(`Role "${form.label}" created`); setForm({ label: "", base: "regional" }); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (role) => {
    if (!window.confirm(`Delete role "${role.label}"? Members revert to the built-in ${role.base} role.`)) return;
    try { await api.delete(`/roles/${role.id}`); toast.success("Role deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const baseLabel = (b) => data.roles.find((r) => r.system && r.base === b)?.label || b;
  const locked = (r) => r.base === "admin" || (userRole === "gm" && r.base === "gm");
  const tierOptions = (b) => b === "manager" ? ["venue", "department"] : ["cluster", "regional"].includes(b) ? ["venue", "region", "cluster", "brand"] : null;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="brand-roles-panel">
      <h2 className="font-heading text-xl font-bold mb-1">Brand roles & scopes</h2>
      <p className="text-sm text-slate-500 mb-5">Rename the built-in roles or create your own titles (Area Manager, Ops Director…). Each title maps to a permission level and a scope tier; operational actions are toggled per role. Members are assigned venues, regions or clusters in Venues &amp; Team.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]">
          <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2">Role title</th>
              <th className="text-left px-3 py-2">Level</th>
              <th className="text-left px-3 py-2">Scope</th>
              {data.op_meta.map((o) => <th key={o.key} className="px-2 py-2 text-center" title={o.desc}>{o.label}</th>)}
              <th className="px-3 py-2 text-right">Members</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.roles.map((r) => (
              <tr key={r.id} data-testid={`role-row-${r.id}`}>
                <td className="px-3 py-2">
                  <input key={r.label} data-testid={`role-label-${r.id}`} defaultValue={r.label} disabled={userRole === "gm" && ["admin", "gm"].includes(r.base)} onBlur={(e) => e.target.value.trim() && e.target.value !== r.label && patch(r, { label: e.target.value }, "Role renamed")}
                    className="h-9 px-2 rounded-md border border-slate-200 text-sm font-bold w-48 focus:border-slate-900 focus:outline-none disabled:bg-slate-50 disabled:text-slate-500" />
                  {r.system && <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-slate-400">built-in</span>}
                </td>
                <td className="px-3 py-2 text-slate-600">{r.system ? "—" : baseLabel(r.base)}</td>
                <td className="px-3 py-2">
                  {tierOptions(r.base) ? (
                    <select data-testid={`role-tier-${r.id}`} value={r.scope_tier} onChange={(e) => patch(r, { scope_tier: e.target.value }, "Scope tier updated")} className="h-9 px-2 rounded-md border border-slate-200 text-xs bg-white">
                      {tierOptions(r.base).map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  ) : <span className="text-xs font-semibold text-slate-500">{r.scope_tier}</span>}
                </td>
                {data.op_meta.map((o) => (
                  <td key={o.key} className="px-2 py-2 text-center">
                    {locked(r) ? <span className="inline-flex items-center justify-center text-emerald-600"><LockSimple size={14} /></span>
                      : <Toggle on={!!r.ops?.[o.key]} testId={`role-op-${r.id}-${o.key}`} onClick={() => patch(r, { ops: { [o.key]: !r.ops?.[o.key] } })} />}
                  </td>
                ))}
                <td className="px-3 py-2 text-right font-bold text-slate-700">{r.members}</td>
                <td className="px-2 py-2">{!r.system && <button data-testid={`role-delete-${r.id}`} onClick={() => remove(r)} className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash size={15} /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mt-5 pt-5 border-t border-slate-200">
        <input data-testid="role-new-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="New role title, e.g. Area Manager" className={inputCls} />
        <select data-testid="role-new-base" value={form.base} onChange={(e) => setForm({ ...form, base: e.target.value })} className={`${inputCls} sm:w-64`}>
          {data.bases.filter((b) => b !== "admin" && (userRole !== "gm" || b !== "gm")).map((b) => <option key={b} value={b}>Level: {baseLabel(b)}</option>)}
        </select>
        <button data-testid="role-new-create" onClick={create} className={`${btnPrimary} flex items-center gap-1 shrink-0`}><Plus size={16} /> Create role</button>
      </div>
    </div>
  );
}
